import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SignatureCanvas from "react-signature-canvas";
import jsPDF from "jspdf";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Download, RotateCcw, Mail } from "lucide-react";

function fmtDate(d) {
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function trimCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return canvas;
  const pad = 4;
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(height - 1, maxY + pad);
  const out = document.createElement("canvas");
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out.getContext("2d").drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

export default function Certificate() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [fullName, setFullName] = useState(user?.name || "");
  const [issued, setIssued] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");
  const sigRef = useRef(null);

  useEffect(() => {
    api.get("/certificate").then(({ data }) => setIssued(data)).catch(() => { /* not yet issued */ });
  }, []);

  // Load logo as data URL for PDF embedding
  const [logoDataUrl, setLogoDataUrl] = useState("");
  useEffect(() => {
    fetch("/logo.jpg").then((r) => r.blob()).then((b) => {
      const reader = new FileReader();
      reader.onloadend = () => setLogoDataUrl(reader.result);
      reader.readAsDataURL(b);
    });
  }, []);

  const clear = () => sigRef.current?.clear();

  const submit = async () => {
    setError("");
    if (!fullName.trim()) { setError("Full name is required."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { setError("Signature is required."); return; }
    const dataUrl = trimCanvas(sigRef.current.getCanvas()).toDataURL("image/png");
    setBusy(true);
    try {
      const { data } = await api.post("/certificate", {
        full_name: fullName.trim(),
        signature_data_url: dataUrl,
      });
      setIssued(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to issue certificate.");
    } finally {
      setBusy(false);
    }
  };

  const buildPdf = () => {
    if (!issued) return null;
    const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 x 792
    const W = 612;
    // Logo in top-left corner
    if (logoDataUrl) {
      try { doc.addImage(logoDataUrl, "JPEG", 60, 60, 60, 60); } catch { /* */ }
    }
    // Outer border
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(2);
    doc.rect(36, 36, W - 72, 720);
    doc.setLineWidth(0.5);
    doc.rect(46, 46, W - 92, 700);

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 64, 175);
    doc.text("CALIFORNIA · CONCEALED CARRY WEAPON TRAINING", W / 2, 100, { align: "center" });

    doc.setTextColor(15, 23, 42);
    doc.setFontSize(28);
    doc.text("Acknowledgement of Completion", W / 2, 150, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("8-Hour Online Training Course", W / 2, 175, { align: "center" });

    // Line
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(1);
    doc.line(120, 200, W - 120, 200);

    // Body
    doc.setFontSize(12);
    doc.setTextColor(51, 65, 85);
    const intro = "This document certifies that the undersigned student has personally completed the full eight (8) hours of instruction in the California Concealed Carry Weapon online training curriculum, including all six modules, module quizzes, and the comprehensive final examination.";
    const lines = doc.splitTextToSize(intro, W - 160);
    doc.text(lines, 80, 240);

    // Student name
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(15, 23, 42);
    doc.text(issued.full_name.toUpperCase(), W / 2, 340, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text("STUDENT", W / 2, 358, { align: "center" });

    // Attestation
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(51, 65, 85);
    const attest = "I hereby attest that I have personally spent eight (8) hours engaged with the course materials, satisfied the minimum study time for each of the six required modules, and passed each module quiz and the comprehensive final examination with a score of 80% or higher. The information provided in this acknowledgement is true and correct to the best of my knowledge.";
    const aLines = doc.splitTextToSize(attest, W - 160);
    doc.text(aLines, 80, 400);

    // Topics covered
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text("MODULES COMPLETED", 80, 495);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const topics = [
      "1.  Firearm Safety & Handling",
      "2.  Shooting Fundamentals & Technique",
      "3.  Legal Use of Force & Self-Defense",
      "4.  Carry, Transport, and Prohibited Places (SB 2)",
      "5.  Mental Health Awareness & Resources",
      "6.  Tactical Awareness & De-escalation",
    ];
    topics.forEach((t, i) => doc.text(t, 80, 515 + i * 14));

    // Signature image
    try {
      doc.addImage(issued.signature_data_url, "PNG", 80, 615, 200, 60);
    } catch { /* ignore */ }
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.5);
    doc.line(80, 680, 290, 680);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("STUDENT SIGNATURE", 80, 695);

    // Date
    const dt = new Date(issued.issued_at);
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(fmtDate(dt), 360, 670);
    doc.setDrawColor(15, 23, 42);
    doc.line(360, 680, W - 80, 680);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("DATE ISSUED", 360, 695);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Final exam score: ${Math.round(issued.final_score * 100)}%   ·   Total study time: 8h+   ·   Course: ${issued.course_title}`, W / 2, 735, { align: "center" });

    return doc;
  };

  const downloadPdf = () => {
    const doc = buildPdf();
    if (!doc) return;
    doc.save(`CCW-Acknowledgement-${issued.full_name.replace(/\s+/g, "_")}.pdf`);
  };

  const emailPdf = async () => {
    setEmailStatus("");
    const doc = buildPdf();
    if (!doc) return;
    const dataUri = doc.output("datauristring"); // data:application/pdf;base64,...
    setEmailing(true);
    try {
      await api.post("/certificate/email", { pdf_base64: dataUri });
      setEmailStatus(`Sent to ${user.email}`);
    } catch (e) {
      setEmailStatus(e.response?.data?.detail || "Email failed.");
    } finally {
      setEmailing(false);
    }
  };

  if (issued) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <button onClick={() => nav("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Acknowledgement Letter</div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          Your completion letter
        </h1>
        <div className="mt-8 border-2 border-slate-900 bg-white p-12 print:p-12 print:border-slate-900" id="certificate-printable">
          <div className="text-center border-b border-slate-900 pb-6">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-blue-700">
              California · Concealed Carry Weapon Training
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mt-4" style={{ fontFamily: "Chivo, sans-serif" }}>
              Acknowledgement of Completion
            </h2>
            <p className="text-sm text-slate-600 mt-1">8-Hour Online Training Course</p>
          </div>
          <p className="mt-8 text-slate-700 leading-relaxed">
            This document certifies that the undersigned student has personally completed the full eight (8)
            hours of instruction in the California Concealed Carry Weapon online training curriculum, including
            all six modules, module quizzes, and the comprehensive final examination.
          </p>
          <div className="text-center my-10">
            <div className="text-3xl font-bold tracking-wide text-slate-900 uppercase" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="cert-name">
              {issued.full_name}
            </div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mt-2">Student</div>
          </div>
          <p className="text-slate-700 leading-relaxed text-sm">
            <em>I hereby attest that I have personally spent eight (8) hours engaged with the course materials,
            satisfied the minimum study time for each of the six required modules, and passed each module quiz
            and the comprehensive final examination with a score of 80% or higher. The information provided in
            this acknowledgement is true and correct to the best of my knowledge.</em>
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <img src={issued.signature_data_url} alt="signature" className="h-20 object-contain" />
              <div className="border-t border-slate-900 mt-1 pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                Student Signature
              </div>
            </div>
            <div>
              <div className="text-slate-900 h-20 flex items-end">{fmtDate(new Date(issued.issued_at))}</div>
              <div className="border-t border-slate-900 mt-1 pt-1 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                Date Issued
              </div>
            </div>
          </div>
          <div className="mt-8 text-xs text-slate-500 text-center">
            Final exam score: {Math.round(issued.final_score * 100)}% · Total study time logged: 8h+
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-4 items-center">
          <button data-testid="download-cert-pdf" onClick={downloadPdf}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-sm font-medium">
            <Download className="h-4 w-4" /> Download PDF
          </button>
          <button data-testid="email-cert" onClick={emailPdf} disabled={emailing}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-900 hover:bg-slate-50 px-6 py-3 rounded-sm font-medium disabled:opacity-50">
            <Mail className="h-4 w-4" /> {emailing ? "Sending…" : "Email me a copy"}
          </button>
          <button data-testid="print-cert" onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-900 hover:bg-slate-50 px-6 py-3 rounded-sm font-medium">
            Print
          </button>
          {emailStatus && <span data-testid="email-status" className="text-sm text-slate-600">{emailStatus}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <button onClick={() => nav("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Acknowledgement Letter</div>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mt-2" style={{ fontFamily: "Chivo, sans-serif" }}>
        Sign your 8-hour attestation
      </h1>
      <p className="text-slate-600 mt-3 text-sm">
        Confirm your full legal name and sign below. Your signed letter will be downloadable as a PDF.
      </p>

      <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6">
        <label className="block text-sm font-semibold text-slate-700 mb-2">Full legal name</label>
        <input
          data-testid="cert-name-input"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="w-full border border-slate-300 rounded-sm px-3 py-2.5 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        />

        <div className="mt-6">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Signature</label>
          <div className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-sm">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0f172a"
              canvasProps={{ "data-testid": "cert-signature-canvas", className: "w-full h-48 cursor-crosshair" }}
            />
          </div>
          <button data-testid="cert-clear-sig" onClick={clear} className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900">
            <RotateCcw className="h-3 w-3" /> Clear signature
          </button>
        </div>

        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700">
          <em>By submitting, I attest under penalty of perjury that I have personally spent eight (8) hours
          engaged with the course materials, met the minimum study time for each module, and passed the
          module quizzes and final exam.</em>
        </div>

        {error && (
          <div data-testid="cert-error" className="mt-4 bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm">
            {error}
          </div>
        )}
        <button
          data-testid="cert-submit-btn"
          onClick={submit}
          disabled={busy}
          className="mt-6 w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-sm font-medium disabled:opacity-50"
        >
          {busy ? "Issuing…" : "Sign & Issue Acknowledgement"}
        </button>
      </div>
    </div>
  );
}
